import { useBedtimeContext } from '../../context/BedtimeContext';
import { fetchLibrary, isSharedPlayback, openLibraryAudio, releaseLibraryAudio, revalidateLibraryAudio } from '../../api/libraryPlayback';
import { useSharedPlaybackAuthority } from '../../hooks/useSharedPlaybackAuthority';
import { restoreProgress, saveProgress } from '../../api/playbackProgress';
import { guardAudioLease } from '../../api/audioLease';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchRender, RenderDetail, RenderListItem } from '../../api/rendersApi';
import { isAbortError } from '../../api/http';
import { useApp } from '../../context/AppContext';
import { filterToHandoff } from './storyModeModel';
import { clearStoryModeSession, markStoryModeStarted, readStoryModeSession, sessionAllowsRender, StoryModeSessionV1 } from './storyModeSession';
import { Player, Scene } from '../../components/ui';
import { MoonIcon } from '../../components/icons';
import { createMeasurementSession, emitMeasurementEvent, flushMeasurementOutbox } from '../../api/measurementApi';
import './StoryTime.css';

const HOLD_TO_EXIT_MS = 2500;
const AUDIO_STALL_TIMEOUT_MS = 15_000;

type PlaybackMeasurement = {
  sessionId: string;
  renderId: string;
  queueLength: number;
  queuePosition: number;
  started: boolean;
  terminal: boolean;
  progressSent: boolean;
  maxProgress: number;
};

function offlineRows(session: StoryModeSessionV1): RenderListItem[] {
  return session.offlineReadyRenderIds.map((renderId) => ({
    render_id: renderId,
    story_id: '',
    voice_id: session.voiceId,
    household_id: session.householdId,
    child_slug: null,
    listener_id: session.listenerId,
    listener_name: null,
    status: 'parent_approved',
    created_at: session.createdAt,
  }));
}

const StoryTime: React.FC = () => {
  const navigate = useNavigate();
  const bedtime = useBedtimeContext();
  const { state, fetchStories } = useApp();
  useEffect(() => { void fetchStories(); }, [fetchStories]);
  const audioRef = useRef<HTMLAudioElement>(null);
  const resumePositionRef = useRef(0);
  const progressSavedAtRef = useRef(0);
  const openRequestRef = useRef(0);
  const detailControllerRef = useRef<AbortController | null>(null);
  const sessionRef = useRef<StoryModeSessionV1 | null>(null);
  const playbackMeasurementRef = useRef<PlaybackMeasurement | null>(null);
  const holdStartRef = useRef<number | null>(null);
  const holdRafRef = useRef<number | null>(null);
  const stallTimerRef = useRef<number | null>(null);
  const stallMediaTimeRef = useRef<number | null>(null);
  const recoveryRenderIdRef = useRef<string | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const pickerReturnFocusRef = useRef<HTMLElement | null>(null);
  const startedPlaybackRef = useRef(false);
  const preparedInitialRef = useRef<string | null>(null);
  const handledEndedPlaybackRef = useRef<number | null>(null);
  const activePlaybackRef = useRef<number | null>(null);
  const [playbackId, setPlaybackId] = useState<number | null>(null);
  const advanceInFlightRef = useRef(false);
  const [handoff, setHandoff] = useState<StoryModeSessionV1 | null>(null);
  const [available, setAvailable] = useState<RenderListItem[]>([]);
  const [current, setCurrent] = useState<RenderDetail | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [showPicker, setShowPicker] = useState(false);
  const [autoAdvanceIndex, setAutoAdvanceIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!showPicker) {
      if (pickerReturnFocusRef.current && document.contains(pickerReturnFocusRef.current)) pickerReturnFocusRef.current.focus();
      return undefined;
    }
    pickerReturnFocusRef.current = document.activeElement as HTMLElement | null;
    const focusTimer = window.setTimeout(() => pickerRef.current?.querySelector<HTMLElement>('button:not([disabled])')?.focus(), 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setShowPicker(false);
        return;
      }
      if (event.key !== 'Tab' || !pickerRef.current) return;
      const focusable = Array.from(pickerRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showPicker]);

  const emitPlayback = useCallback((
    eventName: 'playback_started' | 'playback_progress' | 'playback_completed' | 'playback_cancelled',
    cancelReason?: string,
  ) => {
    const measurement = playbackMeasurementRef.current;
    if (!measurement || measurement.terminal || isSharedPlayback(measurement.renderId)) return;
    if (eventName === 'playback_started') {
      if (measurement.started) return;
      measurement.started = true;
    } else if (!measurement.started) return;
    if (eventName === 'playback_progress') {
      if (measurement.progressSent || measurement.maxProgress < 0.8) return;
      measurement.progressSent = true;
    }
    if (eventName === 'playback_completed' || eventName === 'playback_cancelled') {
      measurement.terminal = true;
    }
    void emitMeasurementEvent({
      event_name: eventName,
      idempotency_key: `${measurement.sessionId}:${eventName}`,
      render_id: measurement.renderId,
      session_id: measurement.sessionId,
      progress_ratio: eventName === 'playback_completed' ? 1 : measurement.maxProgress,
      properties: {
        source: measurement.queueLength > 1 ? 'story_mode_queue' : 'story_mode',
        queue_length: measurement.queueLength,
        queue_position: measurement.queuePosition,
        ...(cancelReason ? { cancel_reason_code: cancelReason } : {}),
      },
    }).catch(() => { /* Measurement failure must not interrupt bedtime playback. */ });
  }, []);

  useEffect(() => {
    const onPageHide = (event: PageTransitionEvent) => {
      // A cached page can resume with the same mounted player/session.
      if (!event.persisted) emitPlayback('playback_cancelled', 'page_exit');
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      emitPlayback('playback_cancelled', 'navigation_exit');
    };
  }, [emitPlayback]);

  const recoverToParent = useCallback(() => {
    if (state.user?.id) clearStoryModeSession(state.user.id);
    navigate('/my-stories?storyMode=unavailable', { replace: true });
  }, [navigate, state.user?.id]);

  useSharedPlaybackAuthority(current, audioRef, recoverToParent);
  useEffect(() => () => releaseLibraryAudio(current), [current]);

  useEffect(() => {
    if (!state.user?.id) return;
    const session = readStoryModeSession(state.user.id, 'auto');
    if (!session) { navigate('/my-stories?storyMode=unavailable', { replace: true }); return; }
    sessionRef.current = session;
    setHandoff(session);
    startedPlaybackRef.current = false;
  }, [navigate, state.user?.id]);

  useEffect(() => {
    if (!handoff) return;
    const controller = new AbortController();
    const offline = (state.offlineSession || (typeof navigator !== 'undefined' && navigator.onLine === false));
    setLoading(true);
    setFailed(false);
    fetchLibrary(handoff, controller.signal)
      .then((renders) => {
        const exact = filterToHandoff(renders, handoff).filter((render) => !offline || sessionAllowsRender(handoff, render.render_id, true));
        if (!exact.length || !exact.some((render) => render.render_id === handoff.initialRenderId)) {
          recoverToParent();
          return;
        }
        setAvailable(exact);
        const queue = handoff.queueRenderIds.length ? handoff.queueRenderIds : [handoff.initialRenderId];
        const initialIndex = Math.max(0, queue.indexOf(handoff.initialRenderId));
        setCurrentIndex(initialIndex);
        setAutoAdvanceIndex(handoff.queueRenderIds.length > 1 ? initialIndex : null);
      })
      .catch((error) => {
        if (isAbortError(error) || controller.signal.aborted) return;
        // In production the service worker can satisfy this exact-pair list
        // from its cache. The placeholder fallback keeps the child surface
        // non-technical if only the approved audio/detail cache is available.
        if (offline && sessionAllowsRender(handoff, handoff.initialRenderId, true)) {
          const cachedRows = offlineRows(handoff);
          if (cachedRows.some((render) => render.render_id === handoff.initialRenderId)) {
            setAvailable(cachedRows);
            setAutoAdvanceIndex(handoff.queueRenderIds.length > 1 ? 0 : null);
          } else {
            recoverToParent();
          }
        } else {
          recoverToParent();
        }
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [handoff, recoverToParent, state.offlineSession]);

  useEffect(() => {
    void flushMeasurementOutbox();
    const onOnline = () => void flushMeasurementOutbox();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  useEffect(() => () => {
    detailControllerRef.current?.abort();
    if (stallTimerRef.current != null) window.clearTimeout(stallTimerRef.current);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !current || isSharedPlayback(current.render_id)) return;
    return guardAudioLease(audio, current.offline_access_expires_at, recoverToParent);
  }, [current, recoverToParent]);

  useEffect(() => {
    if (!current) return;
    let canceled = false;
    const revalidate = async () => {
      audioRef.current?.pause();
      try {
        if (isSharedPlayback(current.render_id)) {
          if (!await revalidateLibraryAudio(current) && !canceled) recoverToParent();
          return;
        }
        const detail = await fetchRender(current.render_id, true);
        if (!canceled) setCurrent(detail);
      } catch { if (!canceled) { recoverToParent(); } }
    };
    window.addEventListener('online', revalidate);
    return () => { canceled = true; window.removeEventListener('online', revalidate); };
  }, [current, recoverToParent]);

  useEffect(() => {
    const audio = audioRef.current;
    const save = () => {
      if (audio && current?.household_id && current.listener_id && !isSharedPlayback(current.render_id)) {
        saveProgress(current.household_id, current.listener_id, current.render_id, audio.currentTime, audio.ended);
      }
    };
    window.addEventListener('pagehide', save);
    window.addEventListener('online', save);
    return () => { save(); window.removeEventListener('pagehide', save); window.removeEventListener('online', save); };
  }, [current]);

  const queue = handoff?.queueRenderIds.length ? handoff.queueRenderIds : handoff ? [handoff.initialRenderId] : [];
  const currentListItem = queue[currentIndex] ? available.find((render) => render.render_id === queue[currentIndex]) : undefined;
  const currentTitle = current?.story_title || (currentListItem?.story_id && state.stories.find((story) => story.story_id === currentListItem.story_id)?.title) || 'Your bedtime story';

  const safeOpen = useCallback(async (renderId: string, autoplay = false) => {
    const handoffSession = sessionRef.current;
    const offline = (state.offlineSession || (typeof navigator !== 'undefined' && navigator.onLine === false));
    if (!handoffSession || !sessionAllowsRender(handoffSession, renderId, offline)) {
      if (startedPlaybackRef.current) {
        recoveryRenderIdRef.current = renderId;
        setFailed(true);
      } else recoverToParent();
      return false;
    }
    const requestId = ++openRequestRef.current;
    detailControllerRef.current?.abort();
    const controller = new AbortController();
    detailControllerRef.current = controller;
    setOpening(true);
    try {
      const detail = await openLibraryAudio(renderId, controller.signal);
      if (requestId !== openRequestRef.current || controller.signal.aborted) { releaseLibraryAudio(detail); return false; }
      if (detail.status !== 'parent_approved' || detail.household_id !== handoffSession.householdId || detail.listener_id !== handoffSession.listenerId || detail.voice_id !== handoffSession.voiceId) {
        releaseLibraryAudio(detail);
        if (startedPlaybackRef.current) {
          recoveryRenderIdRef.current = renderId;
          setFailed(true);
        } else recoverToParent();
        return false;
      }
      const progress = !isSharedPlayback(detail.render_id) && detail.household_id && detail.listener_id
        ? await restoreProgress(detail.household_id, detail.listener_id, detail.render_id) : null;
      if (requestId !== openRequestRef.current || controller.signal.aborted) { releaseLibraryAudio(detail); return false; }
      emitPlayback('playback_cancelled', 'story_changed');
      const measurementQueue = handoffSession.queueRenderIds.length
        ? handoffSession.queueRenderIds : [handoffSession.initialRenderId];
      playbackMeasurementRef.current = {
        sessionId: createMeasurementSession('bedtime'),
        renderId: detail.render_id,
        queueLength: measurementQueue.includes(renderId) ? measurementQueue.length : 1,
        queuePosition: Math.max(0, measurementQueue.indexOf(renderId)) + 1,
        started: false,
        terminal: false,
        progressSent: false,
        maxProgress: 0,
      };
      activePlaybackRef.current = requestId;
      handledEndedPlaybackRef.current = null;
      setPlaybackId(requestId);
      resumePositionRef.current = progress?.completed ? 0 : progress?.position_seconds || 0;
      recoveryRenderIdRef.current = null;
      setCurrent(detail); setEnded(false); setIsPlaying(false);
      if (autoplay) window.setTimeout(() => {
        if (requestId === openRequestRef.current && requestId === activePlaybackRef.current) {
          audioRef.current?.play().catch(() => setIsPlaying(false));
        }
      }, 0);
      return true;
    } catch (error) {
      if (!isAbortError(error) && !controller.signal.aborted) {
        if (startedPlaybackRef.current) {
          recoveryRenderIdRef.current = renderId;
          setFailed(true);
        } else recoverToParent();
      }
      return false;
    } finally { if (requestId === openRequestRef.current) setOpening(false); }
  }, [emitPlayback, recoverToParent, state.offlineSession]);

  // Resolve the approved detail before the child taps. Safari requires play()
  // directly in that gesture, not after an asynchronous fetch and setTimeout.
  useEffect(() => {
    const initial = handoff?.initialRenderId;
    if (!initial || loading || !available.some((render) => render.render_id === initial)
      || preparedInitialRef.current === initial) return;
    preparedInitialRef.current = initial;
    void safeOpen(initial);
  }, [available, handoff?.initialRenderId, loading, safeOpen]);

  const start = async () => {
    const session = sessionRef.current;
    const offline = Boolean(state.offlineSession || navigator.onLine === false);
    if (!session || !current || !sessionAllowsRender(session, current.render_id, offline)) { recoverToParent(); return; }
    if (audioRef.current?.paused) audioRef.current.play().catch(() => setIsPlaying(false));
    else audioRef.current?.pause();
  };

  const onEnded = async (eventAudio: HTMLAudioElement, eventPlaybackId: number | null, eventRenderId: string) => {
    // A manual selection is its own playback, not the original queue entry.
    // Both the media instance and open request must still own completion.
    if (eventAudio !== audioRef.current || eventPlaybackId === null
      || eventPlaybackId !== activePlaybackRef.current || eventPlaybackId !== openRequestRef.current
      || current?.render_id !== eventRenderId) return;
    if (autoAdvanceIndex !== null && queue[currentIndex] !== eventRenderId) return;
    if (advanceInFlightRef.current || handledEndedPlaybackRef.current === eventPlaybackId) return;
    handledEndedPlaybackRef.current = eventPlaybackId;
    clearStallTimer();
    audioRef.current?.pause();
    persistProgress(true);
    setIsPlaying(false); emitPlayback('playback_completed');
    if (autoAdvanceIndex !== null && autoAdvanceIndex < queue.length - 1) {
      const nextIndex = currentIndex + 1;
      const next = queue[nextIndex];
      advanceInFlightRef.current = true;
      setCurrentIndex(nextIndex);
      setAutoAdvanceIndex(nextIndex);
      try {
        await safeOpen(next, true);
      } finally {
        advanceInFlightRef.current = false;
      }
    } else {
      if (state.user?.id) clearStoryModeSession(state.user.id);
      setEnded(true);
    }
  };

  const clearStallTimer = () => {
    if (stallTimerRef.current != null) window.clearTimeout(stallTimerRef.current);
    stallTimerRef.current = null;
    stallMediaTimeRef.current = null;
  };
  const handleAudioFailure = () => {
    emitPlayback('playback_cancelled', 'media_error');
    const failedRenderId = current?.render_id || queue[currentIndex] || sessionRef.current?.initialRenderId || null;
    audioRef.current?.pause();
    clearStallTimer();
    setIsPlaying(false);
    if (startedPlaybackRef.current) {
      recoveryRenderIdRef.current = failedRenderId;
      setFailed(true);
    } else recoverToParent();
  };
  const handlePlaybackStarted = () => {
    const active = state.user?.id ? markStoryModeStarted(state.user.id) : null;
    if (!active) {
      audioRef.current?.pause();
      recoverToParent();
      return;
    }
    sessionRef.current = active;
    startedPlaybackRef.current = true;
    clearStallTimer();
    setIsPlaying(true);
    emitPlayback('playback_started');
  };
  const armStallWatchdog = () => {
    const audio = audioRef.current;
    clearStallTimer();
    if (!audio || audio.paused || audio.ended) return;
    stallMediaTimeRef.current = audio.currentTime;
    const checkProgress = () => {
      const activeAudio = audioRef.current;
      const observedMediaTime = stallMediaTimeRef.current;
      stallTimerRef.current = null;
      if (!activeAudio || activeAudio.paused || activeAudio.ended) {
        stallMediaTimeRef.current = null;
        return;
      }
      if (observedMediaTime != null && activeAudio.currentTime > observedMediaTime + 0.05) {
        stallMediaTimeRef.current = activeAudio.currentTime;
        stallTimerRef.current = window.setTimeout(checkProgress, AUDIO_STALL_TIMEOUT_MS);
        return;
      }
      handleAudioFailure();
    };
    stallTimerRef.current = window.setTimeout(checkProgress, AUDIO_STALL_TIMEOUT_MS);
  };
  const persistProgress = (completed = false) => {
    const audio = audioRef.current;
    if (audio && current?.household_id && current.listener_id && !isSharedPlayback(current.render_id)) {
      saveProgress(current.household_id, current.listener_id, current.render_id, audio.currentTime, completed);
      progressSavedAtRef.current = Date.now();
    }
  };
  const handlePlaybackProgress = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = event.currentTarget;
    const offline = Boolean(state.offlineSession || navigator.onLine === false);
    const leaseExpired = current?.offline_access_expires_at != null
      && !(Date.now() < Date.parse(current.offline_access_expires_at));
    if (leaseExpired || (offline && (!sessionRef.current || !current
      || !sessionAllowsRender(sessionRef.current, current.render_id, true)))) {
      audio.pause();
      recoverToParent();
      return;
    }
    if (Date.now() - progressSavedAtRef.current >= 5000) persistProgress();
    const measurement = playbackMeasurementRef.current;
    if (measurement?.started && !measurement.terminal && !audio.seeking
      && Number.isFinite(audio.duration) && audio.duration > 0
      && Number.isFinite(audio.currentTime) && audio.currentTime >= 0) {
      measurement.maxProgress = Math.max(measurement.maxProgress, Math.min(1, audio.currentTime / audio.duration));
      emitPlayback('playback_progress');
    }
    if (stallTimerRef.current == null) return;
    const mediaTime = event.currentTarget.currentTime;
    if (stallMediaTimeRef.current != null && mediaTime <= stallMediaTimeRef.current + 0.05) return;
    armStallWatchdog();
  };
  const handlePlaybackPaused = () => {
    persistProgress();
    clearStallTimer();
    setIsPlaying(false);
  };

  const cancelHold = () => { holdStartRef.current = null; if (holdRafRef.current != null) cancelAnimationFrame(holdRafRef.current); holdRafRef.current = null; setHoldProgress(0); };
  const updateHold = () => {
    if (holdStartRef.current == null) return;
    const progress = Math.min(1, (performance.now() - holdStartRef.current) / HOLD_TO_EXIT_MS);
    setHoldProgress(progress);
    if (progress >= 1) {
      emitPlayback('playback_cancelled', 'parent_exit');
      cancelHold();
      clearStallTimer();
      if (state.user?.id) clearStoryModeSession(state.user.id);
      if (failed) {
        navigate('/story-time/setup', {
          replace: true,
          state: {
            renderId: recoveryRenderIdRef.current || current?.render_id || sessionRef.current?.initialRenderId,
            recovery: 'playback_interrupted',
          },
        });
      } else {
        navigate('/my-stories?storyMode=exited');
      }
      return;
    }
    holdRafRef.current = requestAnimationFrame(updateHold);
  };
  const beginHold = () => { holdStartRef.current = performance.now(); holdRafRef.current = requestAnimationFrame(updateHold); };
  const holdExit = <button type="button" className="story-time__exit" data-testid="hold-exit" onPointerDown={beginHold} onPointerUp={cancelHold} onPointerCancel={cancelHold} onPointerLeave={cancelHold} aria-label="Hold to return to parent space"><span style={{ transform: `scaleX(${Math.max(.08, holdProgress)})` }} /></button>;

  if (loading || !handoff) return <Scene><main className="story-time story-time--loading" role="status" aria-live="polite"><MoonIcon size={38} /><h1>Getting your bedtime story ready…</h1></main></Scene>;
  if (failed) return <Scene><main className="story-time" data-testid="storytime-failure"><div className="story-time__safe-message"><MoonIcon size={42} /><h1>This story needs a grown-up.</h1><p>It’s quiet for now. Hold the button below to ask a grown-up to fix it.</p></div>{holdExit}</main></Scene>;
  if (ended) return <Scene><main className="story-time" data-testid="goodnight"><div className="story-time__goodnight"><MoonIcon size={50} /><h1>Goodnight{current?.listener_name ? `, ${current.listener_name}` : ''}.</h1><span>The story is finished.</span></div>{holdExit}</main></Scene>;

  const availableAnother = available.filter((render) => render.render_id !== current?.render_id);
  const titleFor = (render: RenderListItem) => render.story_id
    ? state.stories.find((story) => story.story_id === render.story_id)?.title || 'Another bedtime story'
    : 'Another bedtime story';
  const chooseAnother = async (renderId: string) => {
    setShowPicker(false);
    setAutoAdvanceIndex(null);
    await safeOpen(renderId, true);
  };

  const artwork = state.stories.find((story) => story.story_id === (current?.story_id || currentListItem?.story_id));
  return <Scene src={artwork?.thumbnail_url || undefined}><div className="story-time"><main className="story-time__content"><div className="story-time__moon" aria-hidden="true"><MoonIcon size={54} /></div><h1>{currentTitle}</h1><p className="story-time__listener">For {current?.listener_name || bedtime.listeners.find(listener => listener.listener_id === handoff?.listenerId)?.display_name || 'you'}</p>{queue.length > 1 && autoAdvanceIndex !== null && <p className="story-time__progress" data-testid="story-queue-progress">Story {currentIndex + 1} of {queue.length}</p>}<Player mode="child" title={currentTitle} playing={isPlaying} loading={opening || !current} currentTime={0} duration={0} onPlay={() => void start()} onPause={() => void start()} transportTestId="giant-play" transportLabel={isPlaying ? 'Pause story' : `Play ${currentTitle}`} /><p className="story-time__hint">{isPlaying ? 'Your story is playing' : 'Tap play when you’re ready'}</p>{current && <audio key={playbackId} ref={audioRef} src={current.audio_url || undefined} preload="metadata" onLoadedMetadata={(event) => { const audio = event.currentTarget; if (Number.isFinite(audio.duration)) audio.currentTime = Math.min(resumePositionRef.current, audio.duration); }} onPlay={handlePlaybackStarted} onPlaying={clearStallTimer} onPause={handlePlaybackPaused} onEnded={(event) => void onEnded(event.currentTarget, playbackId, current.render_id)} onError={handleAudioFailure} onWaiting={armStallWatchdog} onStalled={armStallWatchdog} onTimeUpdate={handlePlaybackProgress} onCanPlay={clearStallTimer} />}{availableAnother.length > 0 && <button type="button" className="story-time__another" onClick={() => setShowPicker(true)}>Another story</button>}</main>{holdExit}{showPicker && <div className="story-time__picker-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setShowPicker(false); }}><div className="story-time__picker" role="dialog" aria-modal="true" aria-labelledby="story-picker-title" ref={pickerRef}><h2 id="story-picker-title">Choose another story</h2><p>Only stories approved for this bedtime are shown.</p><div className="story-time__picker-list">{availableAnother.map((render) => <button type="button" key={render.render_id} onClick={() => void chooseAnother(render.render_id)}>{titleFor(render)}</button>)}</div><button type="button" className="story-time__picker-cancel" onClick={() => setShowPicker(false)}>Not now</button></div></div>}</div></Scene>;
};

export default StoryTime;
